/**
 * scope.js — Carousel Composer: the grading scopes (waveform, RGB parade, histogram).
 *
 * Exposure is matched by eye against something behind it, so the numbers have to
 * be honest and the paint has to be cheap enough to run while a slider moves.
 *
 * MEASURING. Anything drawable (canvas, image, bitmap) is first drawn small —
 * about 192x144 — and read back once. Two decisions matter there:
 *   - smoothing is OFF. A smoothed downsample averages a blown pixel with its
 *     neighbours and the clipping cue would quietly lie; nearest-neighbour keeps
 *     whatever pixels it lands on exactly as they are, which is how a hardware
 *     scope line-skips too.
 *   - the sample keeps the source's aspect, so a column in the scope is the same
 *     column in the photo.
 * Raw pixels (an ImageData, or anything shaped like one) are accepted as they
 * are and strided down to the same budget, which is also how the tests drive it
 * with no DOM at all.
 *
 * READING. Counts are mapped to brightness through traceAlpha(), a log-ish
 * response: a sky holding one value would otherwise be the only thing visible on
 * a linear scale, and the shadow detail you are trying to judge would not show.
 *
 * DRAWING. draw(canvas, data, kind, theme) paints in CSS pixels — pass
 * theme.dpr and size the canvas in device pixels. Nothing here touches the DOM
 * at import; the scratch canvases are made on first use.
 */

export const SCOPE_KINDS = [
  { key: 'waveform', label: 'Waveform', short: 'W' },
  { key: 'parade', label: 'RGB parade', short: 'P' },
  { key: 'histogram', label: 'Histogram', short: 'H' },
];

const KIND_KEYS = SCOPE_KINDS.map((k) => k.key);

/** Sample budget: a 1600px preview costs one small draw plus this many pixels. */
export const MAX_SAMPLES = 48000;
export const SAMPLE_COLS = 192;
export const SAMPLE_ROWS = 144;

/** Vertical resolution of a waveform / parade, and the histogram's bucket count. */
export const WAVE_BINS = 64;
export const PARADE_COLS = 64;
export const HIST_BINS = 64;

/** Pixels this transparent are not a colour anyone graded; they are skipped. */
const MIN_ALPHA = 8;

export function normalizeKind(kind) {
  return KIND_KEYS.includes(kind) ? kind : 'waveform';
}

export function scopeLabel(kind) {
  const k = SCOPE_KINDS.find((s) => s.key === normalizeKind(kind));
  return k.label;
}

/* ---------------- sampling ---------------- */

const scratch = { sample: null, trace: null };

function getScratch(slot, w, h) {
  let c = scratch[slot];
  if (!c) {
    if (typeof OffscreenCanvas === 'function') c = new OffscreenCanvas(w, h);
    else if (typeof document !== 'undefined') c = document.createElement('canvas');
    else return null;
    scratch[slot] = c;
  }
  if (c.width !== w) c.width = w;
  if (c.height !== h) c.height = h;
  return c;
}

export function isPixels(src) {
  return !!(src && src.data && src.width > 0 && src.height > 0);
}

export function sourceSize(src) {
  if (!src) return { w: 0, h: 0 };
  if (isPixels(src)) return { w: src.width, h: src.height };
  const w = src.naturalWidth || src.videoWidth || src.width || 0;
  const h = src.naturalHeight || src.videoHeight || src.height || 0;
  return { w, h };
}

/**
 * Draws `src` into the shared scratch at scope size and reads the pixels back.
 * `filter` is a CSS filter string (the same one a preview layer paints with, so
 * the scope measures the graded photo rather than the file); `rect` limits the
 * measurement to a region of the source, e.g. the part of a photo a cell shows.
 */
export function samplePixels(src, { cols = SAMPLE_COLS, rows = SAMPLE_ROWS, filter = '', rect = null } = {}) {
  const size = sourceSize(src);
  if (!size.w || !size.h) return null;
  const r = rect && rect.w > 0 && rect.h > 0 ? rect : { x: 0, y: 0, w: size.w, h: size.h };
  const scale = Math.min(1, cols / r.w, rows / r.h);
  const w = Math.max(1, Math.round(r.w * scale));
  const h = Math.max(1, Math.round(r.h * scale));
  const c = getScratch('sample', w, h);
  if (!c) return null;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  // see the header: averaging would hide a clipped pixel
  ctx.imageSmoothingEnabled = false;
  if (filter) ctx.filter = filter;
  try {
    ctx.drawImage(src, r.x || 0, r.y || 0, r.w, r.h, 0, 0, w, h);
  } catch {
    return null;                  // not decoded yet, or a tainted canvas
  } finally {
    ctx.filter = 'none';
  }
  try {
    return ctx.getImageData(0, 0, w, h);
  } catch {
    // A cross-origin source taints the canvas FOR GOOD, and this one is shared:
    // throw it away, or one foreign photo would blank the scope for every photo
    // after it. (Hand the scope a preview blob or a canvas, not a CDN <img>.)
    scratch.sample = null;
    return null;
  }
}

/** Raw pixels are read every Nth pixel in both axes, to hold the same budget. */
export function strideFor(width, height) {
  const n = width * height;
  if (n <= MAX_SAMPLES) return 1;
  // ceil, not round: the budget is a ceiling, and overshooting it is what makes
  // a scope stutter on a drag
  return Math.max(1, Math.ceil(Math.sqrt(n / MAX_SAMPLES)));
}

function toPixels(src, opts) {
  if (!src) return null;
  if (isPixels(src)) return src;
  return samplePixels(src, opts);
}

/* ---------------- maths ---------------- */

/** Rec. 709 luma, which is what a waveform's IRE axis means. */
export function luma(r, g, b) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function binOf(value, bins) {
  const b = Math.floor((value * bins) / 256);
  return b < 0 ? 0 : b >= bins ? bins - 1 : b;
}

/**
 * Count -> 0..1 brightness. Log-ish, so a flat sky does not drown everything
 * else, with a floor so one stray sample still shows as a faint dot.
 */
const RESPONSE = 24;
const FLOOR = 0.07;

export function traceAlpha(count, max) {
  if (!count || max <= 0) return 0;
  const a = Math.log1p((RESPONSE * count) / max) / Math.log1p(RESPONSE);
  return Math.min(1, Math.max(FLOOR, a));
}

/**
 * A luma waveform: one column of the photo per column of the scope, each a
 * count per luma bin. Bin 0 is black (0 IRE) and sits at the bottom when drawn.
 */
export function computeWaveform(pixels, { cols = SAMPLE_COLS, bins = WAVE_BINS } = {}) {
  const { width, height, data } = pixels;
  const nc = Math.max(1, Math.min(cols, width));
  const lum = new Uint32Array(nc * bins);
  const clipLo = new Uint32Array(nc);
  const clipHi = new Uint32Array(nc);
  const colN = new Uint32Array(nc);
  const st = strideFor(width, height);
  let max = 0;
  let n = 0;
  for (let y = 0; y < height; y += st) {
    const row = y * width;
    for (let x = 0; x < width; x += st) {
      const i = (row + x) * 4;
      if (data[i + 3] < MIN_ALPHA) continue;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const c = Math.min(nc - 1, Math.floor((x * nc) / width));
      const k = c * bins + binOf(Math.round(luma(r, g, b)), bins);
      const v = ++lum[k];
      if (v > max) max = v;
      if (r >= 255 || g >= 255 || b >= 255) clipHi[c]++;
      if (r <= 0 || g <= 0 || b <= 0) clipLo[c]++;
      colN[c]++;
      n++;
    }
  }
  return { kind: 'waveform', cols: nc, bins, lum, max, clipLo, clipHi, colN, n };
}

/**
 * An RGB parade: the same measurement done per channel, drawn as three panels.
 * `cols` is columns PER PANEL, and the three share one scale so their relative
 * weight can be read across the panels.
 */
export function computeParade(pixels, { cols = PARADE_COLS, bins = WAVE_BINS } = {}) {
  const { width, height, data } = pixels;
  const nc = Math.max(1, Math.min(cols, width));
  const ch = [new Uint32Array(nc * bins), new Uint32Array(nc * bins), new Uint32Array(nc * bins)];
  const clipLo = new Uint32Array(nc);
  const clipHi = new Uint32Array(nc);
  const colN = new Uint32Array(nc);
  const st = strideFor(width, height);
  let max = 0;
  let n = 0;
  for (let y = 0; y < height; y += st) {
    const row = y * width;
    for (let x = 0; x < width; x += st) {
      const i = (row + x) * 4;
      if (data[i + 3] < MIN_ALPHA) continue;
      const c = Math.min(nc - 1, Math.floor((x * nc) / width));
      for (let p = 0; p < 3; p++) {
        const v = ++ch[p][c * bins + binOf(data[i + p], bins)];
        if (v > max) max = v;
      }
      if (data[i] >= 255 || data[i + 1] >= 255 || data[i + 2] >= 255) clipHi[c]++;
      if (data[i] <= 0 || data[i + 1] <= 0 || data[i + 2] <= 0) clipLo[c]++;
      colN[c]++;
      n++;
    }
  }
  return { kind: 'parade', cols: nc, bins, ch, max, clipLo, clipHi, colN, n };
}

/** A histogram: how many pixels hold each level, per channel and for luma. */
export function computeHistogram(pixels, { bins = HIST_BINS } = {}) {
  const { width, height, data } = pixels;
  const ch = [new Uint32Array(bins), new Uint32Array(bins), new Uint32Array(bins)];
  const lum = new Uint32Array(bins);
  const st = strideFor(width, height);
  let max = 0;
  let lumMax = 0;
  let clipLo = 0;
  let clipHi = 0;
  let n = 0;
  for (let y = 0; y < height; y += st) {
    const row = y * width;
    for (let x = 0; x < width; x += st) {
      const i = (row + x) * 4;
      if (data[i + 3] < MIN_ALPHA) continue;
      for (let p = 0; p < 3; p++) {
        const v = ++ch[p][binOf(data[i + p], bins)];
        if (v > max) max = v;
      }
      const lv = ++lum[binOf(Math.round(luma(data[i], data[i + 1], data[i + 2])), bins)];
      if (lv > lumMax) lumMax = lv;
      if (data[i] >= 255 || data[i + 1] >= 255 || data[i + 2] >= 255) clipHi++;
      if (data[i] <= 0 || data[i + 1] <= 0 || data[i + 2] <= 0) clipLo++;
      n++;
    }
  }
  return { kind: 'histogram', bins, ch, lum, max, lumMax, clipLo, clipHi, n };
}

/** Measure a source (or raw pixels) for one kind of scope. */
export function computeScope(src, kind, opts = {}) {
  const k = normalizeKind(kind);
  const pixels = toPixels(src, opts);
  if (!pixels) return null;
  if (k === 'histogram') return computeHistogram(pixels, opts);
  if (k === 'parade') return computeParade(pixels, opts);
  return computeWaveform(pixels, opts);
}

/** Where a column's samples sit, in bins. Used by the tests and any readout. */
export function columnStats(data, col) {
  const bins = data.bins;
  const arr = data.kind === 'parade' ? data.ch[0] : data.lum;
  let lo = -1, hi = -1, n = 0, sum = 0;
  for (let b = 0; b < bins; b++) {
    const v = arr[col * bins + b];
    if (!v) continue;
    if (lo < 0) lo = b;
    hi = b;
    n += v;
    sum += v * b;
  }
  return { n, lo, hi, mean: n ? sum / n : -1 };
}

/* ---------------- drawing ---------------- */

export const DEFAULT_THEME = {
  dpr: 1,
  bg: null,                       // the box's own background shows through
  grid: 'rgba(255,255,255,.11)',
  gridEdge: 'rgba(255,255,255,.22)',
  text: 'rgba(230,230,234,.62)',
  trace: '#CFE9EE',
  r: '#FF6B6B',
  g: '#56D68A',
  b: '#6AA6FF',
  clip: '#E0A45C',
  font: '8px "DM Mono", ui-monospace, monospace',
};

const IRE = [0, 25, 50, 75, 100];
/** Wide enough for "100" at the label font, or the graticule would eat it. */
const GUTTER = 18;
const PAD = 2;

function rgbOf(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  if (!m) return [255, 255, 255];
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

/**
 * The plot rectangle, and whether there is room to label the graticule. At the
 * phone's size the numbers would be a smudge, so only the lines are drawn.
 */
export function plotBox(w, h, kind) {
  const horizontal = kind === 'histogram';
  const labels = horizontal ? h >= 52 && w >= 92 : h >= 56 && w >= 96;
  const left = PAD + (labels && !horizontal ? GUTTER : 0);
  const bottom = PAD + (labels && horizontal ? 9 : 0);
  return {
    x: left,
    y: PAD,
    w: Math.max(1, w - left - PAD),
    h: Math.max(1, h - PAD - bottom),
    labels,
  };
}

function drawGraticule(ctx, box, th, horizontal) {
  ctx.save();
  ctx.lineWidth = 1;
  ctx.font = th.font;
  ctx.textBaseline = 'middle';
  ctx.textAlign = horizontal ? 'center' : 'right';
  for (const ire of IRE) {
    const t = ire / 100;
    const edge = ire === 0 || ire === 100;
    ctx.strokeStyle = edge ? th.gridEdge : th.grid;
    ctx.beginPath();
    if (horizontal) {
      // half a pixel keeps a 1px line from smearing across two
      const x = Math.round(box.x + box.w * t) + 0.5;
      ctx.moveTo(x, box.y);
      ctx.lineTo(x, box.y + box.h);
    } else {
      const y = Math.round(box.y + box.h * (1 - t)) + 0.5;
      ctx.moveTo(box.x, y);
      ctx.lineTo(box.x + box.w, y);
    }
    ctx.stroke();
    if (!box.labels) continue;
    ctx.fillStyle = th.text;
    if (horizontal) {
      ctx.textAlign = t === 0 ? 'left' : t === 1 ? 'right' : 'center';
      ctx.fillText(String(ire), box.x + box.w * t, box.y + box.h + 5);
    } else {
      ctx.textBaseline = t === 1 ? 'top' : t === 0 ? 'bottom' : 'middle';
      ctx.fillText(String(ire), box.x - 3, box.y + box.h * (1 - t));
    }
  }
  ctx.restore();
}

/**
 * Paints a counts-per-(column, bin) block. One putImageData plus one drawImage,
 * because a rect per cell is ~12k fills and would drop frames on a drag.
 */
function paintTrace(ctx, rect, cols, bins, counts, max, color) {
  const c = getScratch('trace', cols, bins);
  if (!c) return;
  const tctx = c.getContext('2d', { willReadFrequently: true });
  if (!tctx) return;
  const img = tctx.createImageData(cols, bins);
  const px = img.data;
  const [cr, cg, cb] = rgbOf(color);
  for (let x = 0; x < cols; x++) {
    for (let b = 0; b < bins; b++) {
      const n = counts[x * bins + b];
      if (!n) continue;
      // row 0 is the top of the image, bin 0 is black: flip
      const i = ((bins - 1 - b) * cols + x) * 4;
      px[i] = cr;
      px[i + 1] = cg;
      px[i + 2] = cb;
      px[i + 3] = Math.round(traceAlpha(n, max) * 255);
    }
  }
  tctx.putImageData(img, 0, 0);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(c, 0, 0, cols, bins, rect.x, rect.y, rect.w, rect.h);
  ctx.restore();
}

/** Blown or crushed columns get a bar on the edge they ran into. */
function paintClips(ctx, rect, cols, clipLo, clipHi, colN, th) {
  ctx.save();
  ctx.fillStyle = th.clip;
  const cw = rect.w / cols;
  for (let x = 0; x < cols; x++) {
    const n = colN[x] || 1;
    if (clipHi[x]) {
      ctx.globalAlpha = Math.min(1, 0.3 + (clipHi[x] / n) * 2);
      ctx.fillRect(rect.x + x * cw, rect.y, Math.max(1, cw), 2);
    }
    if (clipLo[x]) {
      ctx.globalAlpha = Math.min(1, 0.3 + (clipLo[x] / n) * 2);
      ctx.fillRect(rect.x + x * cw, rect.y + rect.h - 2, Math.max(1, cw), 2);
    }
  }
  ctx.restore();
}

function drawWaveform(ctx, data, box, th) {
  drawGraticule(ctx, box, th, false);
  if (!data || !data.n) return;
  paintTrace(ctx, box, data.cols, data.bins, data.lum, data.max, th.trace);
  paintClips(ctx, box, data.cols, data.clipLo, data.clipHi, data.colN, th);
}

function drawParade(ctx, data, box, th) {
  const gap = 3;
  const pw = (box.w - gap * 2) / 3;
  const colors = [th.r, th.g, th.b];
  for (let p = 0; p < 3; p++) {
    const rect = { x: box.x + p * (pw + gap), y: box.y, w: pw, h: box.h };
    drawGraticule(ctx, { ...rect, labels: p === 0 && box.labels }, th, false);
    if (!data || !data.n) continue;
    paintTrace(ctx, rect, data.cols, data.bins, data.ch[p], data.max, colors[p]);
    paintClips(ctx, rect, data.cols, data.clipLo, data.clipHi, data.colN, th);
  }
}

function drawHistogram(ctx, data, box, th) {
  if (!data || !data.n) {
    drawGraticule(ctx, box, th, true);
    return;
  }
  const colors = [th.r, th.g, th.b];
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let p = 0; p < 3; p++) {
    const arr = data.ch[p];
    ctx.beginPath();
    ctx.moveTo(box.x, box.y + box.h);
    for (let i = 0; i < data.bins; i++) {
      const x = box.x + (box.w * (i + 0.5)) / data.bins;
      ctx.lineTo(x, box.y + box.h * (1 - traceAlpha(arr[i], data.max)));
    }
    ctx.lineTo(box.x + box.w, box.y + box.h);
    ctx.closePath();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = colors[p];
    ctx.fill();
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = colors[p];
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.restore();
  // the grid goes on TOP here: the channels are composited additively, and
  // anything underneath them glows through as a brighter line
  drawGraticule(ctx, box, th, true);
  // crushed and blown totals stand on the ends of the level axis
  ctx.save();
  ctx.fillStyle = th.clip;
  if (data.clipLo) {
    ctx.globalAlpha = Math.min(1, 0.3 + (data.clipLo / data.n) * 2);
    ctx.fillRect(box.x, box.y, 2, box.h);
  }
  if (data.clipHi) {
    ctx.globalAlpha = Math.min(1, 0.3 + (data.clipHi / data.n) * 2);
    ctx.fillRect(box.x + box.w - 2, box.y, 2, box.h);
  }
  ctx.restore();
}

/**
 * Paints `data` into `canvas`. The canvas is sized in DEVICE pixels by the
 * caller; everything here is laid out in CSS pixels through theme.dpr.
 * A null `data` still draws the graticule, so the box never blinks empty.
 */
export function draw(canvas, data, kind, theme) {
  if (!canvas || typeof canvas.getContext !== 'function') return false;
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const th = { ...DEFAULT_THEME, ...(theme || {}) };
  const dpr = th.dpr || 1;
  const w = canvas.width / dpr;
  const h = canvas.height / dpr;
  const k = normalizeKind(kind || (data && data.kind));
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  if (th.bg) {
    ctx.fillStyle = th.bg;
    ctx.fillRect(0, 0, w, h);
  }
  const box = plotBox(w, h, k);
  const shown = data && data.kind === k ? data : null;
  if (k === 'histogram') drawHistogram(ctx, shown, box, th);
  else if (k === 'parade') drawParade(ctx, shown, box, th);
  else drawWaveform(ctx, shown, box, th);
  return true;
}
